import { showToastWithTimeout } from "../actions/message";
import { handleError, normalizeError } from "../../utils/errorHandling";
import { buildActionProps, wrapResult } from "../../utils/apiResultWrapper";
import { withDownloadToast } from "../../utils/exports/withDownloadToast";

export default (store) => (next) => (action) => {
  if (action.meta && action.meta.api) {
    next(action);
    const promise = action
      .apiClient({
        method: action.meta.api.method,
        url: action.meta.api.endpoint,
        data: action.meta.api.payload,
        params: action.meta.api.params,
        signal: action.meta.api.signal,
        headers: action.meta.api.headers,
        responseType: action.meta.api.file ? "blob" : "json",
      })
      .then(async (res) => {
        if (
          action.meta.api.file &&
          (action.meta.api.apiImage || res.status !== 202)
        ) {
          const fileName = action.meta.api.fileName;
          const format =
            action.meta.api.format === "excel" ||
            action.meta.api.format === "fancy-excel"
              ? ".xlsx"
              : `.${action.meta.api.format}`;
          const downloadName = fileName + format;
          await withDownloadToast(downloadName, () => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", downloadName);
            link.click();
          });
        }
        const results = !action.meta.api.file
          ? { ...res.data, duration: res.config.duration }
          : res.data;
        next({
          ...action,
          type: action.type.replace("_START", "_SUCCESS"),
          results,
        });

        return res;
      })
      .catch(throwBlobResponseDataAsJson)
      .catch((err) => {
        if (err?.response?.status === 401) {
          next({ type: action.type.replace("_START", "_FAIL") });
          store.dispatch({
            type: "AUTH_FAIL",
            meta: {
              token: {
                destroy: true,
              },
            },
          });

          throw err;
        }

        if (err?.response?.status === 422 && action.meta.forPreview) {
          const parameters = JSON.parse(
            err.response.config?.data ?? "{}"
          ).parameters;

          if (parameters && action.meta.chart) {
            // cant use set422Errors action directly here
            store.dispatch({
              type: "SET_ACTIVE_EXEC_ERRORS",
              visualizationId: action.visualizationId,
              ...buildActionProps({
                err,
                parameters,
                parameterizedFilterPrefix:
                  action.meta.chart.parameterizedFilterPrefix,
              }),
            });
          }
        }

        if (err.message === "canceled") {
          throw err;
          // take no action, this is only used for abort controllers now
        }

        const normalizedError = normalizeError(err);
        next({
          ...action,
          type: action.type.replace("_START", "_FAIL"),
          error: normalizedError,
          duration: err.duration,
        });

        if (action.meta.api.toastOnFailure) {
          const errors = normalizedError.errors ?? normalizedError.data;
          // Validation errors.
          const handleErrors = errors
            ? Object.values(errors).map((error) => error[0])
            : normalizedError.message;

          showToastWithTimeout(handleErrors, "danger", next);
        }
        handleError(err, {
          showToast: false,
          reportAllErrors: action.meta.api.reportAllErrors,
        });

        throw err;
      });
    return wrapResult(promise);
  } else {
    next(action);
  }
};

/**
 * Ensures that the axios exception gets rethrown, except if the response data
 * was a blob, it tries to convert it to JSON first.
 * @param err
 * @return {*}
 */
function throwBlobResponseDataAsJson(err) {
  if (err?.response?.data instanceof window.Blob) {
    return err.response.data.text().then((text) => {
      try {
        const jsonParsed = JSON.parse(text);
        if (jsonParsed) {
          err.response.data = jsonParsed;
        }
      } catch (e) {}
      throw err;
    });
  }
  throw err;
}
